import React, { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useAppDispatch, useAppSelector } from "~/store/hooks";

import { DialogBox, DialogBoxFooterType, Loader, LoaderTypes, NoLink, SelectInput } from "~/core";
import { getTheUserGuid } from "~/login";
import { actions as notificationActions } from "~/notifications";
import { LayerAPI, LayerUtilsAPI } from "@ai360/core";

import { ACTIVE_YN } from "~/core/picklist";

import { messages } from "../../../i18n-messages";
import * as selectors from "../../selectors";
import { colorOptionRenderer, getBackgroundGradientStyle } from "../../../../utils";

interface IManualSurfacePropertiesProps {
    isOpen: boolean;
    layer: LayerAPI.IManualSurfaceLayer;
    letterIcon: string;
    onClose: () => void;
    onSave: (surface: {
        attributeGuid: string;
        colorSchemeGuid: string;
        surfaceGuid: string;
    }) => void;
    surface: LayerAPI.ISubLayer;
    type: number;
}

const getSurfaceGuidFromProps = (
    layer: LayerAPI.IManualSurfaceLayer,
    surface: LayerAPI.ISubLayer
): string => layer.selectedSurfaceGuid || surface.surfaceGuid;

export const ManualSurfaceProperties = ({
    isOpen,
    layer,
    letterIcon,
    onClose,
    onSave,
    surface,
    type,
}: IManualSurfacePropertiesProps): JSX.Element => {
    const { formatMessage } = useIntl();
    const dispatch = useAppDispatch();
    const colorSchemeOptions = useAppSelector(selectors.getColorSchemeOptions);
    const userGuid = useAppSelector(getTheUserGuid);

    const [attributeGuid, setAttributeGuid] = useState<string>(layer.selectedAttributeGuid);
    const [colorSchemeGuid, setColorSchemeGuid] = useState<string>(surface.colorSchemeGuid);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [surfaceGuid, setSurfaceGuid] = useState<string>(getSurfaceGuidFromProps(layer, surface));

    useEffect(() => {
        if (isOpen) {
            setColorSchemeGuid(surface.colorSchemeGuid);
            setAttributeGuid(layer.selectedAttributeGuid);
            setSurfaceGuid(getSurfaceGuidFromProps(layer, surface));
        }
    }, [isOpen]);

    const onApiError = useCallback(
        (err: unknown, message: unknown) => {
            dispatch(notificationActions.apiCallError(err, null, message));
        },
        [dispatch]
    );

    const updateColorScheme = useCallback(
        (
            newColorSchemeGuid: string,
            newAttributeGuid: string = attributeGuid,
            newSurfaceGuid: string = surfaceGuid
        ) => {
            setColorSchemeGuid(newColorSchemeGuid);
            setAttributeGuid(newAttributeGuid);
            setSurfaceGuid(newSurfaceGuid);
        },
        [attributeGuid, surfaceGuid]
    );

    const updateAttribute = useCallback(
        (newAttributeGuid: string, newSurfaceGuid: string) => {
            const attributeName = LayerUtilsAPI.getAttributeGuidFieldName(type);
            const otherSurface = layer.subLayers.find(
                (s) => s.surfaceGuid === newSurfaceGuid && s[attributeName] === newAttributeGuid
            );
            updateColorScheme(otherSurface.colorSchemeGuid, newAttributeGuid, newSurfaceGuid);
        },
        [layer, type, updateColorScheme]
    );

    const handleSave = useCallback(() => {
        onSave({
            attributeGuid,
            colorSchemeGuid,
            surfaceGuid,
        });
    }, [onSave, attributeGuid, colorSchemeGuid, surfaceGuid]);

    const resetToDefault = useCallback(() => {
        const attributeName = LayerUtilsAPI.getAttributeGuidFieldName(type);

        if (
            layer.originalAttributeGuid &&
            [LayerUtilsAPI.LayerType.SOIL, LayerUtilsAPI.LayerType.REC].includes(type)
        ) {
            updateColorScheme(layer.originalColorSchemeGuid, layer.originalAttributeGuid);
        } else if (type === LayerUtilsAPI.LayerType.EVENT_MANUAL) {
            setIsLoading(true);
            LayerAPI.getDefaultAttributeGuid(userGuid, {
                AgEventGeneralGuid: surface.agEventGeneralGuid,
                AgEventTransactionTypeGuid: surface.agEventTransactionTypeGuid,
                isManual: true,
            })
                .then((result) => {
                    const firstAttributeLayer = layer.subLayers.find(
                        (slyr) => slyr[attributeName] === result[attributeName]
                    );
                    updateAttribute(
                        firstAttributeLayer[attributeName],
                        firstAttributeLayer.surfaceGuid
                    );
                })
                .catch((err) => {
                    onApiError(err, messages.failedToResetToDefault);
                })
                .finally(() => setIsLoading(false));
        } else {
            updateColorScheme(surface.colorSchemeGuid, layer.selectedAttributeGuid);
        }
    }, [type, layer, surface, userGuid, updateColorScheme, updateAttribute, onApiError]);

    const attributeName = LayerUtilsAPI.getAttributeGuidFieldName(type);

    let placeholderText = formatMessage(messages.attribute);
    const isSoil = type === LayerUtilsAPI.LayerType.SOIL;
    if (isSoil) {
        placeholderText = formatMessage(messages.soilTypeAttribute);
    }

    const attributeOptions = layer.subLayers
        .map((subLayer) => ({
            label: subLayer.displayName,
            value: !isSoil ? subLayer.surfaceGuid : subLayer[attributeName],
        }))
        .sort((a, b) => a.label.localeCompare(b.label));

    const colorSchemeSelectOptions = colorSchemeOptions.map((colorScheme) => ({
        background: getBackgroundGradientStyle(colorScheme, true),
        label: " ",
        value: colorScheme.colorSchemeGuid,
        titleValue: colorScheme.name,
    }));

    const colorScheme = colorSchemeOptions.find((cs) => cs.colorSchemeGuid === colorSchemeGuid);
    const backgroundImageStyle = getBackgroundGradientStyle(colorScheme, true);
    const backgroundImageCss = {
        backgroundImage: `${backgroundImageStyle}`,
    };

    return (
        <DialogBox
            className="surface-properties-dialog"
            draggable
            footerType={DialogBoxFooterType.ACTION_CANCEL}
            forceOverflow
            isOpen={isOpen}
            letterIcon={letterIcon}
            onAction={handleSave}
            onClose={onClose}
            title={`${layer.displayName} - ${formatMessage(messages.layerProperties)}`}
        >
            {type === LayerUtilsAPI.LayerType.REC ||
            type === LayerUtilsAPI.LayerType.EVENT_MANUAL ? (
                <div className="attribute-label">{surface.displayName}</div>
            ) : (
                <SelectInput
                    clearable={false}
                    optionIsHiddenKey={ACTIVE_YN}
                    onChange={(e) => {
                        const newLayer = layer.subLayers.find((slyr) =>
                            isSoil ? slyr[attributeName] === e : slyr.surfaceGuid === e
                        );
                        updateAttribute(newLayer[attributeName], newLayer.surfaceGuid);
                    }}
                    options={attributeOptions}
                    placeholderText={placeholderText}
                    value={isSoil ? attributeGuid : surfaceGuid}
                />
            )}
            <SelectInput
                inputCSS={backgroundImageCss}
                clearable={false}
                optionIsHiddenKey={ACTIVE_YN}
                filterable={false}
                onChange={(e) => updateColorScheme(e)}
                options={colorSchemeSelectOptions}
                optionRenderer={colorOptionRenderer}
                placeholderText={formatMessage(messages.colorScheme)}
                value={colorSchemeGuid}
            />
            <NoLink label={formatMessage(messages.resetToDefault)} onClick={resetToDefault} />
            {!isLoading ? null : <Loader type={LoaderTypes.LINE_SCALE_PULSE_OUT} />}
        </DialogBox>
    );
};
